import React, { useEffect, useRef } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Paper, Grid, Typography, Button, Input, Box } from '@material-ui/core';
import _ from 'lodash';
import { UserIcon, PWIcon } from '../../Icon';
import { i18n } from '../../../language';
import { authAPI } from '../../../apis';
import { alertMsg } from '../../../apis/commlogic';
import { envFunc } from '../../../utils/environments';

const LoginBtn = withStyles((theme) => ({
  root: {
    height: '3rem',
    borderRadius: '0.625rem',
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: '#4c93ff',
    borderColor: '#4c93ff',
    color: '#fff',
    fontFamily: 'NotoSans-Medium',
    '&:hover': {
      backgroundColor: '#538FFF',
      borderColor: '#538FFF',
      boxShadow: 'none',
    },
  },
}))(Button);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: '2.5rem',
  },
  paper: {
    width: '33.938rem',
    height: '30.625rem',
    position: 'relative',
    padding: '2.563rem 2.563rem 2.5rem 2.5rem',
    borderRadius: '0.937rem',
    boxShadow: '0 -6.3px 12px 0 rgba(0, 0, 0, 0.04)',
  },
  icon: {
    width: '1.5rem',
    height: '1.5rem',
    color: '#5f9df6',
    marginRight: '0.5rem',
  },
  inputBox: {
    height: '3.5rem',
    padding: '0 1.063rem 0 1.125rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    marginBottom: '0.623rem',
    '& input': {
      color: '#525e6c',
      fontSize: '1rem',
      fontFamily: 'NotoSans-Medium',
    },
  },
  disableInputBox: {
    height: '3.5rem',
    padding: '0 1.063rem 0 1.125rem',
    border: 'solid 1px #e4e7eb',
    backgroundColor: 'rgba(245, 245, 245, 1)',
    borderRadius: '10px',
    marginBottom: '0.623rem',
    '& input': {
      color: '#c0c6cb',
      fontSize: '1rem',
      fontFamily: 'NotoSans-Medium',
    },
  },
  hintBottom: {
    marginTop: '1.5rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#525e6c',
    fontFamily: 'NotoSans-Medium',
  },
  titleText: {
    color: '#001d63',
    fontWeight: 'bold',
    fontSize: '1.875rem',
    fontFamily: 'NotoSans-Bold',
  },
  logo: {
    fontFamily: 'NotoSans-Bold',
    color: '#001d63',
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  grid: {
    height: '100%',
    justifyContent: 'space-between',
  },
  empty: {
    flexGrow: 1,
  },
  footA: {
    color: '#538fff',
  },
}));

function Base({ successHandle, failHandle, children, button, title, corpId }) {
  const classes = useStyles();
  const [LoginForm, setLoginForm] = React.useState({
    corpID: corpId,
    passcode: null,
  });

  const submitClickHandle = async ({ corpID, passcode }) => {
    if (!corpID || !passcode) {
      alertMsg(i18n.t('ALERT_PLEASE_ENTER_THE_CORRECT_PASSWORD'), _.uniqueId('warning_'), 'warning');
      return;
    }

    try {
      const { success, token, refreshToken, displayName, createTime, refreshTokenExp } = await authAPI.Login({
        corpID,
        passcode,
      });
      const temp = {
        corpID,
        token,
        refreshToken,
        displayName,
        createTime,
        refreshTokenExp,
      };
      if (success) {
        if (typeof successHandle === 'function') {
          successHandle(temp);
        }
      } else if (typeof failHandle === 'function') {
        failHandle(temp);
      }
    } catch (error) {
      if (typeof failHandle === 'function') {
        failHandle(error);
      }
    }
  };

  const keydownEvent = (e) => {
    const EnterCode = 13;
    if (EnterCode === e.keyCode) {
      (async () => {
        await submitClickHandle(LoginForm);
      })();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', keydownEvent);
    return () => {
      document.removeEventListener('keydown', keydownEvent);
    };
  }, [LoginForm]);

  const loginFootRef = useRef(null);
  useEffect(() => {
    (async () => {
      const elmA = document.createElement('a');
      elmA.setAttribute('href', await envFunc.getEnvLoginUrl());
      elmA.setAttribute('target', '_blank');
      elmA.setAttribute('class', classes.footA);
      elmA.innerText = i18n.t('LOGIN_BUSINESS_SUPPORT_DESK');
      loginFootRef.current.innerHTML = i18n.t(
        'LOGIN_IF_YOU_ENCOUNTER_ANY_PROBLEMS_PLEASE_USE__FOR_ASSISTANCE_FOR_URGENT_SUPPORT_AFTER_OFFICE_HOURS_PLEASE_CALL_IT_CALL_CENTRE_ON_2515_2653',
        { value: elmA.outerHTML }
      );
    })();
  }, []);

  return (
    <div className={classes.root}>
      <Paper elevation={3} className={classes.paper}>
        <Grid container direction="column" className={classes.grid}>
          <Box display="flex" justifyContent="space-between" style={{ margin: '-0.85rem 0 2.188rem 0' }}>
            <Typography className={classes.titleText} gutterBottom>
              {title}
            </Typography>
            <span>{children}</span>
          </Box>
          <Input
            value={LoginForm.corpID ?? ''}
            className={classes.disableInputBox}
            startAdornment={<UserIcon className={classes.icon} style={{ color: '#c0c6cb' }} />}
            disableUnderline={true}
            placeholder={i18n.t('LOGIN_INPUT_USER')}
            disabled
            onChange={(event) => {
              setLoginForm({ ...LoginForm, corpID: event.target.value });
            }}
          />
          <Input
            value={LoginForm.passcode ?? ''}
            className={classes.inputBox}
            startAdornment={<PWIcon className={classes.icon} />}
            disableUnderline={true}
            placeholder={i18n.t('LOGIN_INPUT_PASSWORD')}
            focused="true"
            type="password"
            onChange={(event) => {
              setLoginForm({ ...LoginForm, passcode: event.target.value });
            }}
          />
          <div className={classes.empty} />
          <LoginBtn
            onClick={() => {
              (async () => {
                await submitClickHandle(LoginForm);
              })();
            }}
          >
            {button}
          </LoginBtn>
          <Typography className={classes.hintBottom} variant="body2" gutterBottom ref={loginFootRef} />
        </Grid>
      </Paper>
    </div>
  );
}

export default Base;
